import { toCsv } from "@/utils/csv"
import type {
  SequenceSpamTestRun,
  SequenceSpamTestSummary,
  SequenceSpamTestVariant,
} from "@/types/entities/sequence"

const RUN_CSV_HEADERS = [
  "variationName",
  "variantId",
  "domain",
  "subject",
  "preview",
  "inbox",
  "spam",
  "missing",
  "failed",
  "pending",
  "total",
  "status",
] as const

const summaryValue = (
  summary: SequenceSpamTestSummary | undefined,
  key: keyof SequenceSpamTestSummary,
): number | string => {
  const value = summary?.[key]
  return typeof value === "number" ? value : ""
}

type SeedBlock = {
  domain: string
  summary: SequenceSpamTestSummary | undefined
}

// Mirror the panel's per-domain seed blocks: one block per tested domain when
// domainResults are present, otherwise a single aggregate fallback so multi
// select domain runs export every domain instead of just the combined totals.
const variationSeedBlocks = (
  variation: SequenceSpamTestVariant,
): SeedBlock[] => {
  const domainResults = variation.domainResults ?? []
  if (domainResults.length) {
    return domainResults.map((result) => ({
      domain: result.domain?.name || "",
      summary: result.summary,
    }))
  }
  return [{ domain: "All domains", summary: variation.summary }]
}

export const runVariationsToCsv = (
  variations: SequenceSpamTestVariant[],
  preview: (variant: SequenceSpamTestVariant) => string,
): string => {
  const rows: (string | number)[][] = [[...RUN_CSV_HEADERS]]

  variations.forEach((variation, index) => {
    const variationName = variation.variationName || `Variation ${index + 1}`
    const variantId = variation.variantId || ""
    const subject = variation.subject || variation.copy?.subject || ""
    const previewText = preview(variation)

    variationSeedBlocks(variation).forEach(({ domain, summary }) => {
      rows.push([
        variationName,
        variantId,
        domain,
        subject,
        previewText,
        summaryValue(summary, "inbox"),
        summaryValue(summary, "spam"),
        summaryValue(summary, "missing"),
        summaryValue(summary, "failed"),
        summaryValue(summary, "pending"),
        summaryValue(summary, "total"),
        variation.generationStatus || summary?.status || "",
      ])
    })
  })

  return toCsv(rows)
}

export const runCsvFileName = (run?: SequenceSpamTestRun): string => {
  const stamp = new Date().toISOString().slice(0, 10)
  const provider = run?.recipientProvider || "seeds"
  return `spam-test-run-${provider}-${stamp}.csv`
}
